import { Injectable } from '@angular/core';
import { NEWS_ROUTE } from '../http-routes';
import { INewsPost } from '../../models/news/news';
import { NEWS_PROFILE_ROUTE } from './news-http-routes';
import {
  INewProfileNews,
  INewsCopyrightResponse,
  INewsListResponse,
  INewsStreamingInfoRequest,
  INewTeamNews,
} from '../../models/news/news-request';
import {
  IDefaultListResponse,
  IDefaultResponse,
  IDefaultResponseT,
} from '../../models/default-response/response';
import { HttpService } from '../http.service';
import {
  NewsReviewStatus,
  NewsWithReviewResponse,
} from '@api/models/news/news-review';
import { LivestreamStatus } from '@api/models/livestream/streaming-info';
import { INewsFixateRequest } from '@api/models/news/news-fixate-request';
import { SportTypeService } from '@api/services/sport-type/sport-type.service';
import { SportType } from '@lib/models/sport-type/sport-type';

export enum NewsTargetType {
  State = 'state',
  StateOnly = 'state_only',
  League = 'league',
  Team = 'team',
  Country = 'country',
}

@Injectable({
  providedIn: 'root',
})
export class NewsHttpService {
  constructor(private http: HttpService) {}

  public async getNewsWithLivestreams(
    skip = 0,
    limit = 0,
    live = false
  ): Promise<IDefaultListResponse<INewsPost>> {
    const route =
      SportTypeService.selectedSportType === SportType.Handball
        ? 'handball/news'
        : NEWS_ROUTE;
    return this.http.get<IDefaultListResponse<INewsPost>>([route, skip, limit]);
  }

  public async getNews(id: string): Promise<INewsPost> {
    return this.http.get<INewsPost>([NEWS_ROUTE, id]);
  }

  public async getRelatedNews(
    id: string,
    limit: number = 2
  ): Promise<INewsListResponse> {
    return this.http.get<INewsListResponse>(
      [NEWS_ROUTE, 'related', id],
      [`limit=${limit}`]
    );
  }

  public async getNewsText(id: string): Promise<Partial<INewsPost>> {
    return this.http.get<Partial<INewsPost>>(
      [NEWS_ROUTE, id],
      ['text_only=true']
    );
  }

  public async getNewsWithSelectedTags(id: string): Promise<INewsPost> {
    return this.http.get<INewsPost>([NEWS_ROUTE, id], ['edit=true']);
  }

  public async deleteTeamNews(id: string): Promise<IDefaultResponse> {
    return this.http.delete([NEWS_ROUTE, 'team', id]).toPromise();
  }

  public async postTeamNews(news: INewTeamNews): Promise<IDefaultResponse> {
    return this.http
      .post<IDefaultResponse>([NEWS_ROUTE, 'team'], news)
      .toPromise();
  }

  public async putTeamNews(
    id: string,
    news: INewTeamNews
  ): Promise<IDefaultResponse> {
    return this.http
      .put<IDefaultResponse>([NEWS_ROUTE, 'team', id], news)
      .toPromise();
  }

  public async postProfileNews(
    news: INewProfileNews
  ): Promise<IDefaultResponse> {
    return this.http
      .post<IDefaultResponse>([NEWS_ROUTE, NEWS_PROFILE_ROUTE], news)
      .toPromise();
  }

  public async putProfileNews(
    id: string,
    news: INewProfileNews
  ): Promise<IDefaultResponse> {
    return this.http
      .put<IDefaultResponse>([NEWS_ROUTE, NEWS_PROFILE_ROUTE, id], news)
      .toPromise();
  }

  public async deleteProfileNews(id: string): Promise<IDefaultResponse> {
    return this.http.delete([NEWS_ROUTE, NEWS_PROFILE_ROUTE, id]).toPromise();
  }

  public async getNewsWithReviewStatus(
    status: NewsReviewStatus,
    skip = 0,
    limit = 0
  ): Promise<NewsWithReviewResponse> {
    return this.http.get<any>([NEWS_ROUTE, 'reviews', status, skip, limit]);
  }

  public async getLatestNewsPaginated(
    skip = 0,
    limit = 0,
    beautiful = false,
    pictures = false,
    sex: string = null
  ): Promise<INewsListResponse> {
    const urlParams = [];
    if (beautiful) {
      urlParams.push('beautiful=true');
    }
    if (pictures) {
      urlParams.push('pictures=true');
    }

    if (sex) {
      urlParams.push(`sex=${sex}`);
    }

    return this.http.get<INewsListResponse>(
      [NEWS_ROUTE, 'latest', skip, limit],
      urlParams
    );
  }

  public async getTopNews(limit: number = 8): Promise<INewsPost[]> {
    return this.http.get([NEWS_ROUTE, 'top'], [`limit=${limit}`]);
  }

  public async getStateTopNews(
    stateId: string,
    limit: number = 8
  ): Promise<IDefaultResponseT<INewsPost[]>> {
    const urlParams = [];
    urlParams.push('beautiful=true');
    urlParams.push('pictures=true');
    urlParams.push(`limit=${limit}`);
    return this.http.get([NEWS_ROUTE, 'state', 'top', stateId], urlParams);
  }

  public async getNewsByPerformanceLevel(
    level: number,
    skip = 0,
    limit = 0,
    beautiful = false,
    studio = false
  ): Promise<INewsListResponse> {
    const urlParams = [];
    if (beautiful) {
      urlParams.push('beautiful=true');
      urlParams.push('pictures=true');
    }
    if (studio) {
      urlParams.push('studio=true');
    }
    return this.http.get([NEWS_ROUTE, 'level', level, skip, limit], urlParams);
  }

  public async getFanatNewsPaginated(
    skip = 0,
    limit = 0
  ): Promise<INewsListResponse> {
    return this.http.get<INewsListResponse>([
      NEWS_ROUTE,
      'fan-at',
      skip,
      limit,
    ]);
  }

  /**
   * Fetches paginated news for the given type which are created by the current user
   */
  public async getNewsByCreatorPaginated(
    creatorType: 'team' | 'newsprofile' | 'author',
    creatorId: string,
    skip = 0,
    limit = 0,
    showIncomplete = true,
    filterProfileNews = false,
    beautiful = false
  ): Promise<INewsListResponse> {
    const urlParams = [];
    if (showIncomplete) {
      urlParams.push('show_incomplete=true');
    }
    if (filterProfileNews) {
      urlParams.push('team_created=true');
    }
    if (beautiful) {
      urlParams.push('beautiful=true');
      urlParams.push('pictures=true');
    }
    return this.http.get<INewsListResponse>(
      [NEWS_ROUTE, 'by', creatorType, creatorId, skip, limit],
      urlParams
    );
  }

  /**
   * Fetches paginated news for the given type
   * TODO: replace with getNewsForTarget in future
   */
  public async getNewsForTargetPaginated(
    targetType: NewsTargetType,
    targetId: string,
    skip = 0,
    limit = 0,
    showIncomplete = false,
    filterProfileNews = false,
    beautiful = false,
    sex: string = null
  ): Promise<INewsListResponse> {
    const urlParams = [];
    if (showIncomplete) {
      urlParams.push('show_incomplete=true');
    }
    if (filterProfileNews) {
      urlParams.push('team_created=true');
    }
    if (beautiful) {
      urlParams.push('beautiful=true');
      urlParams.push('pictures=true');
    }

    if (sex) {
      urlParams.push(`sex=${sex}`);
    }

    return this.http.get<INewsListResponse>(
      [NEWS_ROUTE, 'for', targetType, targetId, skip, limit],
      urlParams
    );
  }

  /**
   * Use this in future instead of getNewsForTargetPaginated
   */
  public async getNewsForTarget(
    targetType: NewsTargetType,
    targetId: string,
    skip = 0,
    limit = 0,
    queryParams?: {
      show_incomplete?: boolean;
      team_created?: boolean;
      sex?: string;
      studio?: boolean;
      editorial?: boolean;
      trending?: boolean;
    }
  ): Promise<INewsListResponse> {
    const query = this.http.buildQueryParams(queryParams);
    return this.http.get<INewsListResponse>(
      [NEWS_ROUTE, 'for', targetType, targetId, skip, limit],
      query
    );
  }

  public async getNewsForUser(
    skip = 0,
    limit = 0,
    beautiful = false
  ): Promise<INewsListResponse> {
    const urlParams = [];
    if (beautiful) {
      urlParams.push('beautiful=true');
      urlParams.push('pictures=true');
    }
    return this.http.get<INewsListResponse>(
      [NEWS_ROUTE, 'user', skip, limit],
      urlParams
    );
  }

  public async deleteTeamNewsPictureByUrl(
    newsId: string,
    pictureUrl: string
  ): Promise<any> {
    return this.http
      .delete([NEWS_ROUTE, 'team', 'picture', newsId], [], { url: pictureUrl })
      .toPromise();
  }

  public async deleteProfileNewsPictureByUrl(
    newsId: string,
    pictureUrl: string
  ): Promise<any> {
    return this.http
      .delete([NEWS_ROUTE, 'profile', 'picture', newsId], [], {
        url: pictureUrl,
      })
      .toPromise();
  }

  public async postCheckCopyright(
    texts: string[]
  ): Promise<INewsCopyrightResponse> {
    try {
      return this.http
        .post<INewsCopyrightResponse>([NEWS_ROUTE, 'check', 'copyright'], {
          strings: texts,
        })
        .toPromise();
    } catch (e) {
      throw e;
    }
  }

  public async patchLiveStreamInfo(
    newsId: string,
    info: INewsStreamingInfoRequest
  ): Promise<IDefaultResponse> {
    return this.http
      .patch<IDefaultResponse>([NEWS_ROUTE, 'live-stream/info', newsId], info)
      .toPromise();
  }

  public async patchLiveStreamStatus(
    newsId: string,
    status: LivestreamStatus
  ): Promise<IDefaultResponse> {
    return this.http
      .patch<IDefaultResponse>([NEWS_ROUTE, 'live-stream/status', newsId], {
        status,
      })
      .toPromise();
  }

  public async getFixatedNews(): Promise<INewsPost[]> {
    const response = await this.http.get<IDefaultListResponse<INewsPost>>(
      [NEWS_ROUTE, 'fixated'],
      [],
      false
    );
    return response.data;
  }

  public async setFixatedNewsEntry(
    id: string,
    data: INewsFixateRequest
  ): Promise<IDefaultResponse> {
    return this.http
      .patch<IDefaultResponse>([NEWS_ROUTE, 'fixation', id], data)
      .toPromise();
  }

  public async deleteNewsFixation(id: string): Promise<IDefaultResponse> {
    return this.http.delete([NEWS_ROUTE, 'fixation', id]).toPromise();
  }

  // Route for Austrian Soccer Board (ASB) Widget
  public async getASBNews(
    skip = 0,
    limit = 0,
    forumId?: string
  ): Promise<IDefaultListResponse<INewsPost>> {
    const queryParams = [];
    if (forumId) {
      queryParams.push(`forum_id=${forumId}`);
    }
    return this.http.get([NEWS_ROUTE, 'asb', skip, limit], queryParams);
  }
}
